import { equals, includes, map, pipe, prop, propOr, trim } from 'ramda';
import type { User, UserStatus } from '@/api/auth';
import { findBoolean } from './common';
import { BONUS_RESTRICTED_COUNTRIES, type Country } from './conf';

export enum USER_GROUP {
  VIP = 'VIP',
  SILVER = 'PVIP',
  GOLD = 'VIP1',
  PLATINUM = 'VIP2',
  DIAMOND = 'VIP3',
  DEPOSITOR = 'DEP',
  BA = 'BA',
  BR = 'BR',
  WPE = 'WPE',
  SignUps = '00002',
  ONE_OR_MORE_DEP = 'DEP1',
  ACTIVE_BONUS = 'ACTBONUS',
  LA = 'LA',
  REF = 'REF',
  LOYALTY_VIP = 'Loyalty_VIP',
  LOYALTY = 'Loyalty',
  SWEDISH = 'Swedish',
  AUD100 = '100aud',
  BONUS_HUNTERS = 'BH',
  JACKPOT_USERS = 'Jackpot_Users', // 5+ deposits
  FIAT_PLAYERS = 'FiatPlayers',
  VERIFIED = 'Tag_Verified',
  DEV_TEST = 'DevTest', //group for QA
  VIPBA = 'VIPBA',
  RAKEBACK = 'RAKEBACK',
  Loyals = 'Loyals',
  Champions = 'Champions',
  TSG_COMPLETE = 'TSG_COMPLETE',
  TSG_START = 'TSG_START',
  SUPER_SPOT = 'SVIP',
  Duplicate = 'Duplicate',
  DEP5 = 'DEP5',
  NOT_CONFIRMED_NDB = 'NotConfirmed_NDB',
  Bonanza_Group_A = 'group_a',
  Bonanza_Group_B = 'group_b',
  Bonanza_Group_C = 'group_c',
  Bonanza_Group_D = 'group_d',
  Bonanza_Group_E = 'group_e',
  Bonanza_Group_F = 'group_f',
  Bonanza_Group_G = 'group_g',
  Bonanza_Group_H = 'group_h',
  PENNY = 'Penny',
  WELCOME_NEWBIES = 'WN_Missions',
  TEST = 'TEST',
  // Risk Segments
  RISK_SEGMENT_S1 = 'risk-segment:S1',
  RISK_SEGMENT_S2 = 'risk-segment:S2',
  RISK_SEGMENT_S3 = 'risk-segment:S3',
  RISK_SEGMENT_S4 = 'risk-segment:S4',
  RISK_SEGMENT_S5 = 'risk-segment:S5',
  RISK_SEGMENT_S6 = 'risk-segment:S6',
  RISK_SEGMENT_S7 = 'risk-segment:S7',
  RISK_SEGMENT_S8 = 'risk-segment:S8',
  RISK_SEGMENT_S9 = 'risk-segment:S9',
  RISK_SEGMENT_S10 = 'risk-segment:S10',
  // Documents
  ID_REQUESTED = 'id_requested',
  ADDRESS_REQUESTED = 'address_requested',
  PAYMENT_REQUESTED = 'payment_requested',
  ID_VERIFIED = 'id_verified',
  ADDRESS_VERIFIED = 'address_verified',
  PAYMENT_VERIFIED = 'payment_verified',
  LOW_BET_LIMIT = 'low_bet_limit',
}

export const COMMON_VIP_GROUPS = [
  USER_GROUP.GOLD,
  USER_GROUP.PLATINUM,
  USER_GROUP.DIAMOND,
];

export const vipGroups = [...COMMON_VIP_GROUPS, USER_GROUP.SILVER];

export const groupMember = (groupId: string) =>
  pipe<[User | undefined], UserStatus[], UserStatus['id'][], boolean>(
    propOr<UserStatus[]>([], 'statuses'),
    map(pipe(prop('id'), trim)),
    findBoolean(equals(groupId))
  );

export const everyGroupMember = (groupId: string[]) => (user?: User) =>
  map((id) => groupMember(id)(user), groupId).every((v) => !!v);

export const someGroupMember = (groupId: string[]) => (user?: User) =>
  map((id) => groupMember(id)(user), groupId).some((v) => !!v);

export const matchGroupMember = (groupId: string[]) => (user?: User) =>
  map((id) => groupMember(id)(user), groupId);

export const groupMemberNoDeposits = groupMember(USER_GROUP.SignUps);

export const groupMemberAUD100 = groupMember(USER_GROUP.AUD100);

export const groupMembeRakeback = groupMember(USER_GROUP.RAKEBACK);

export const groupMemberActiveBonus = groupMember(USER_GROUP.ACTIVE_BONUS);

export const groupMemberDepositor = someGroupMember([USER_GROUP.DEPOSITOR]);

export const groupMemberVIP = someGroupMember(vipGroups);

export const groupMemberSwedish = groupMember(USER_GROUP.SWEDISH);

export const groupMemberBA = groupMember(USER_GROUP.BA);

export const groupMemberSILVER = groupMember(USER_GROUP.SILVER);

export const groupMemberGOLD = groupMember(USER_GROUP.GOLD);

export const groupMemberPLATINUM = groupMember(USER_GROUP.PLATINUM);

export const groupMemberDIAMOND = groupMember(USER_GROUP.DIAMOND);

export const groupMemberJackpotUsers = groupMember(USER_GROUP.JACKPOT_USERS);

export const groupMemberVerified = groupMember(USER_GROUP.VERIFIED);

export const groupMemberDuplicate = groupMember(USER_GROUP.Duplicate);

export const groupMemberTEST = groupMember(USER_GROUP.TEST);

export const groupMemberNotConfirmedNDB = groupMember(
  USER_GROUP.NOT_CONFIRMED_NDB
);

export const groupMemberIdRequested = groupMember(USER_GROUP.ID_REQUESTED);

export const groupMemberAddressRequested = groupMember(
  USER_GROUP.ADDRESS_REQUESTED
);

export const groupMemberPaymentRequested = groupMember(
  USER_GROUP.PAYMENT_REQUESTED
);

export const groupMemberIdVerified = groupMember(USER_GROUP.ID_VERIFIED);

export const groupMemberAddressVerified = groupMember(
  USER_GROUP.ADDRESS_VERIFIED
);

export const groupMemberPaymentVerified = groupMember(
  USER_GROUP.PAYMENT_VERIFIED
);

export const groupMemberDocumentsRequested = someGroupMember([
  USER_GROUP.ID_REQUESTED,
  USER_GROUP.ADDRESS_REQUESTED,
  USER_GROUP.PAYMENT_REQUESTED,
]);

export const groupMemberDocumentsVerified = everyGroupMember([
  USER_GROUP.ID_VERIFIED,
  USER_GROUP.ADDRESS_VERIFIED,
  USER_GROUP.PAYMENT_VERIFIED,
]);

export const groupMemberFiatPlayers = groupMember(USER_GROUP.FIAT_PLAYERS);

export const RISK_SEGMENT_GROUPS = [
  USER_GROUP.RISK_SEGMENT_S1,
  USER_GROUP.RISK_SEGMENT_S2,
  USER_GROUP.RISK_SEGMENT_S3,
  USER_GROUP.RISK_SEGMENT_S4,
  USER_GROUP.RISK_SEGMENT_S5,
  USER_GROUP.RISK_SEGMENT_S6,
  USER_GROUP.RISK_SEGMENT_S7,
  USER_GROUP.RISK_SEGMENT_S8,
  USER_GROUP.RISK_SEGMENT_S9,
  USER_GROUP.RISK_SEGMENT_S10,
];

export const groupMemberRiskSegment = someGroupMember(RISK_SEGMENT_GROUPS);

export const groupMemberBR = groupMember(USER_GROUP.BR);

function groupBuilder(group: USER_GROUP): UserStatus;
function groupBuilder(group: USER_GROUP[]): UserStatus[];
function groupBuilder(group: any) {
  if (Array.isArray(group)) {
    return group.map((g) => ({ id: g, name: g }));
  }

  return { id: group, name: group };
}

const newcomerGroups = groupBuilder([USER_GROUP.SignUps, USER_GROUP.WPE]);

const bonusRestrictedGroup = groupBuilder(USER_GROUP.BR);

export const bonusRestrictedGroups = [USER_GROUP.BA, USER_GROUP.BR];

export const promoRestrictedGroups = [
  ...bonusRestrictedGroups,
  USER_GROUP.VIPBA,
];

export const checkByRestrictedCountries = (country: string) =>
  includes(country as Country, BONUS_RESTRICTED_COUNTRIES);

export const setGroupsForNewcomer = (user?: User, countryCode?: string) => {
  if (!user) return;

  const statuses = user.statuses || [];

  const country = user.country || countryCode || null;

  const pushBonusRestrictedGroup = (groups: UserStatus[]) => [
    ...groups,
    bonusRestrictedGroup,
  ];

  const updatedStatuses =
    statuses.length === 0
      ? newcomerGroups
      : statuses.flatMap((status) => {
          if (country && checkByRestrictedCountries(country)) {
            return pushBonusRestrictedGroup([status]);
          }
          return [status];
        });

  return { ...user, statuses: updatedStatuses };
};
